import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { brandShadowEffect } from '@/lib/styles';

const buttonVariants = cva(
  'inline-flex items-center text-dark-900 justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary border border-primary shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive border border-destructive shadow-sm hover:bg-destructive/90',
        success: 'bg-success text-success border border-success shadow-sm hover:bg-success/90',
        outline: 'border border-input border-border shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        suggestion: 'bg-blue-600 text-white rounded-full',
        ghost: 'hover:bg-dark-200 active:bg-dark-400',
        link: 'text-foreground underline-offset-4 hover:underline',
        shadow: `bg-primary text-primary rounded border border-border ${brandShadowEffect}`,
        shadowSurface: `bg-surface text-dark-900 rounded border border-border ${brandShadowEffect}`,
        dark: 'bg-dark-700 text-white dark:text-primary shadow hover:bg-dark-800 dark:bg-primary dark:hover:bg-primary/80 focus-visible:bg-dark-800 dark:focus-visible:bg-primary/80',
      },
      size: {
        default: 'h-9 p-2',
        xs: 'h-8 rounded-md px-2 text-sm gap-1.5',
        sm: 'h-8 rounded-md px-3 text-sm',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
